/**
 * Centralized API error handler for authentication, authorization, and impersonation scenarios.
 *
 * This module is intentionally separate from error-utils.ts to avoid circular dependencies
 * (error-utils.ts ← auth.ts, but this file needs auth.ts).
 */

import { isSkippableError } from '@/utils/error-utils';
import { isForbiddenError, isTokenRelatedAuthError } from '@/utils/isAuthError';
import { showImpersonationErrorToast } from '@/utils/permissions-widget/impersonation-error-toast';
import { isImpersonating } from '@/utils/permissions-widget/token';

export const AUTH_RETRY_HEADER = 'X-Pathway-Auth-Retry';

export const isReadOnlyRequest = (request: Request): boolean => {
  const method = request.method.toUpperCase();
  return method === 'GET' || method === 'HEAD';
};

const parseResponseErrorBody = async (
  response: Response,
): Promise<Record<string, unknown>> => {
  try {
    const text = await response.clone().text();
    if (!text) {
      return {};
    }

    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Non-JSON or empty bodies are expected for some error responses.
  }

  return {};
};

export const buildErrorInfoFromResponse = async (
  response: Response,
  request: Request,
): Promise<Record<string, unknown>> => {
  const body = await parseResponseErrorBody(response);

  return {
    ...body,
    status: response.status,
    statusText: response.statusText,
    url: request.url,
  };
};

/**
 * Outcome of {@link attemptSessionRecovery} for the response interceptor.
 *
 * - **`propagate`** — Not a recoverable session-expiry error (e.g. permission 403,
 *   impersonation, or a write that could not be retried). Return the original error
 *   response so React Query / callers handle it normally.
 * - **`synthetic`** — Session recovery failed or was skipped after one attempt on a
 *   read request. Replace the error with a fake 200 `{}` response so in-flight queries
 *   do not crash or report to Datadog while re-auth redirect is in progress.
 * - **`retry`** — Silent refresh succeeded and the retried request returned OK.
 *   Return `response` from the retry so the caller receives real data.
 */
export type SessionRecoveryResult =
  | { action: 'propagate' }
  | { action: 'synthetic' }
  | { action: 'retry'; response: Response };

/**
 * Attempt silent token refresh and a single retry for session-expiry API errors.
 *
 * Returns:
 * - `propagate` when recovery does not apply, redirect is starting, or a write
 *   failed after retry (existing mutation error handling applies)
 * - `retry` when refresh + one retry succeeds
 * - `synthetic` when a read request cannot be recovered (redirect in progress)
 *
 * Uses dynamic import of auth helpers to avoid a static circular dependency.
 */
export const attemptSessionRecovery = async (
  errorInfo: unknown,
  _response: Response,
  request: Request,
): Promise<SessionRecoveryResult> => {
  if (isImpersonating() || !isTokenRelatedAuthError(errorInfo)) {
    return { action: 'propagate' };
  }

  if (request.headers.get(AUTH_RETRY_HEADER)) {
    return isReadOnlyRequest(request)
      ? { action: 'synthetic' }
      : { action: 'propagate' };
  }

  const { refreshTokensSilently } = await import('@/utils/auth');
  const newToken = await refreshTokensSilently();

  if (!newToken) {
    // TODO(PATHWAY-3919): Replay pending mutations after re-auth redirect.
    return isReadOnlyRequest(request)
      ? { action: 'synthetic' }
      : { action: 'propagate' };
  }

  if (!isReadOnlyRequest(request)) {
    // Request body may already be consumed; mutation replay is PATHWAY-3919.
    return { action: 'propagate' };
  }

  const retryHeaders = new Headers(request.headers);
  retryHeaders.set('Authorization', `Bearer ${newToken}`);
  retryHeaders.set(AUTH_RETRY_HEADER, '1');

  const retryResponse = await fetch(
    new Request(request, { headers: retryHeaders }),
  );

  if (retryResponse.ok) {
    return { action: 'retry', response: retryResponse };
  }

  return isReadOnlyRequest(request)
    ? { action: 'synthetic' }
    : { action: 'propagate' };
};

/**
 * Centralized API error handler for authentication, authorization, and impersonation scenarios.
 *
 * Handles the following error types:
 * - **Skippable errors** (AbortError, network offline): Returns false
 * - **During impersonation (401/403/Okta errors)**: Shows toast and returns true
 * - **Token-related auth errors (not impersonating)**: Returns true so interceptors can recover
 * - **Other API 401/403 (not impersonating)**: Returns false (let error propagate)
 *
 * @param err - The error to handle
 * @param source - Label for logging (e.g., 'getApiToken', 'Interceptor')
 * @returns true if the error was handled (toast shown or session recovery delegated)
 *
 * @example
 * ```ts
 * const wasHandled = await handleApiError(err, 'fetchData');
 * if (!wasHandled) {
 *   // Error needs to propagate
 *   throw err;
 * }
 * ```
 */
export const handleApiError = async (
  err: unknown,
  source: string,
): Promise<boolean> => {
  // Skip handling for aborted requests, network errors, etc.
  if (isSkippableError(err)) {
    return false;
  }

  const isCurrentlyImpersonating = isImpersonating();

  // During impersonation, show toast for auth-related errors (401/403/Okta)
  if (isCurrentlyImpersonating) {
    if (isForbiddenError(err)) {
      console.warn(`[${source}] Forbidden error during impersonation:`, err);
      showImpersonationErrorToast(err);
      return true;
    }

    if (isTokenRelatedAuthError(err)) {
      console.warn(`[${source}] Auth error during impersonation:`, err);
      showImpersonationErrorToast(err);
      return true;
    }
  }

  if (isTokenRelatedAuthError(err)) {
    console.warn(`[${source}] Token-related auth error:`, err);
    return true;
  }

  return false;
};
